import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { profileAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import LoadingAnimation from "../components/LoadingAnimation";
import { useCleanupOnUnload } from "../hooks/useCleanupOnUnload";
import AuthBackground from "../components/AuthBackground";

const ETHNICITY_OPTIONS = [
  "Asian",
  "Black",
  "Hispanic",
  "Middle Eastern",
  "Native American",
  "Pacific Islander",
  "South Asian",
  "White",
  "Other",
];

const LANGUAGE_OPTIONS = [
  "English",
  "Spanish",
  "Hindi",
  "French",
  "German",
  "Italian",
  "Arabic",
  "Mandarin",
  "Portuguese",
  "Japanese",
];

const SEXUAL_ORIENTATION_OPTIONS = [
  "straight",
  "gay",
  "lesbian",
  "bisexual",
  "other",
];

const STAR_SIGN_OPTIONS = [
  "Aries",
  "Taurus",
  "Gemini",
  "Cancer",
  "Leo",
  "Virgo",
  "Libra",
  "Scorpio",
  "Sagittarius",
  "Capricorn",
  "Aquarius",
  "Pisces",
];

const EXERCISE_OPTIONS = ["never", "sometimes", "regularly"];
const DRINKING_OPTIONS = ["never", "socially", "often"];
const SMOKING_OPTIONS = ["never", "socially", "often"];
const PET_OPTIONS = ["none", "dog", "cat", "other"];
const EDUCATION_OPTIONS = [
  "high_school",
  "diploma",
  "bachelor",
  "master",
  "phd",
  "other",
];
const MARITAL_STATUS_OPTIONS = ["single", "divorced", "widowed"];
const KIDS_OPTIONS = ["no_kids", "have_kids", "want_kids"];

function ProfileSetup() {
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [aboutMe, setAboutMe] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [lookingFor, setLookingFor] = useState("");
  const [photos, setPhotos] = useState([]);
  const [ethnicities, setEthnicities] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [nationalityInput, setNationalityInput] = useState("");
  const [location, setLocation] = useState({
    country: "",
    state: "",
    city: "",
  });
  const [languages, setLanguages] = useState([]);
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [sexualOrientation, setSexualOrientation] = useState("");
  const [starSign, setStarSign] = useState("");
  const [exercise, setExercise] = useState("");
  const [drinking, setDrinking] = useState("");
  const [smoking, setSmoking] = useState("");
  const [pets, setPets] = useState("");
  const [educationLevel, setEducationLevel] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [kids, setKids] = useState("");
  const [religion, setReligion] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const { updateUser } = useAuth();
  const navigate = useNavigate();
  const totalSteps = 4;
  const aboutMeLimit = 500;

  // Clear localStorage when tab is closed
  useCleanupOnUnload();

  const toggleSelection = (value, list, setter) => {
    const newList = list.includes(value)
      ? list.filter((item) => item !== value)
      : [...list, value];
    setter(newList);

    // Trigger validation for multi-select fields if needed
    if (setter === setLanguages && touched.languages) {
      const errors = validateField("languages", newList);
      setFieldErrors((prev) => ({
        ...prev,
        ...errors,
        languages: errors.languages,
      }));
    }
    if (setter === setEthnicities && touched.ethnicities) {
      const errors = validateField("ethnicities", newList);
      setFieldErrors((prev) => ({
        ...prev,
        ...errors,
        ethnicities: errors.ethnicities,
      }));
    }
  };

  const addNationality = () => {
    const trimmed = nationalityInput.trim();
    if (!trimmed) return;
    if (nationalities.includes(trimmed)) {
      setNationalityInput("");
      return;
    }
    const newList = [...nationalities, trimmed];
    setNationalities(newList);
    setNationalityInput("");

    // Trigger validation
    setTouched((prev) => ({ ...prev, nationalities: true }));
    const errors = validateField("nationalities", newList);
    setFieldErrors((prev) => ({ ...prev, ...errors }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image size must be less than 5MB");
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Only image files are allowed");
      return;
    }

    if (photos.length >= 5) {
      setError("Maximum 5 photos allowed");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("photo", file);

      const response = await profileAPI.uploadPhoto(formData);
      const newPhotos = [...photos, response.data.data.url];
      setPhotos(newPhotos);

      // Trigger validation after successful upload
      setTouched((prev) => ({ ...prev, photos: true }));
      const errors = validateField("photos", newPhotos);
      setFieldErrors((prev) => ({ ...prev, ...errors }));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    setPhotos(newPhotos);

    // Trigger validation after photo removal
    if (touched.photos) {
      const errors = validateField("photos", newPhotos);
      setFieldErrors((prev) => ({ ...prev, ...errors }));
    }
  };

  const validateField = (fieldName, value) => {
    const errors = {};

    switch (fieldName) {
      case "name":
        if (!value || value.trim().length < 2) {
          errors.name = "Name must be at least 2 characters";
        }
        break;
      case "age":
        if (!value) {
          errors.age = "Age is required";
        } else if (Number(value) < 18 || Number(value) > 100) {
          errors.age = "Age must be between 18 and 100";
        }
        break;
      case "gender":
        if (!value) {
          errors.gender = "Please select a gender";
        }
        break;
      case "lookingFor":
        if (!value) {
          errors.lookingFor = "Please select a preference";
        }
        break;
      case "ethnicities":
        if (!value || value.length < 1) {
          errors.ethnicities = "Please select at least one ethnicity";
        }
        break;
      case "nationalities":
        if (!value || value.length < 1) {
          errors.nationalities = "Please add at least one nationality";
        }
        break;
      case "location":
        if (!value || !value.country || !value.country.trim()) {
          errors.location = "Country is required";
        } else if (!value.state || !value.state.trim()) {
          errors.location = "State is required";
        } else if (!value.city || !value.city.trim()) {
          errors.location = "City is required";
        }
        break;
      case "languages":
        if (!value || value.length < 1) {
          errors.languages = "Please select at least one language";
        }
        break;
      case "aboutMe":
        if (!value || value.trim().length < 10) {
          errors.aboutMe = "About Me must be at least 10 characters";
        } else if (value.length > aboutMeLimit) {
          errors.aboutMe = `About Me must be ${aboutMeLimit} characters or less`;
        }
        break;
      case "height":
        if (!value) {
          errors.height = "Height is required";
        } else if (Number(value) < 100 || Number(value) > 250) {
          errors.height = "Height must be between 100 and 250 cm";
        }
        break;
      case "weight":
        if (!value) {
          errors.weight = "Weight is required";
        } else if (Number(value) < 30 || Number(value) > 200) {
          errors.weight = "Weight must be between 30 and 200 kg";
        }
        break;
      case "sexualOrientation":
        if (!value) {
          errors.sexualOrientation = "Please select your sexual orientation";
        }
        break;
      case "starSign":
        if (!value) {
          errors.starSign = "Please select your star sign";
        }
        break;
      case "photos":
        if (!value || value.length === 0) {
          errors.photos = "Please upload at least one photo";
        }
        break;
      case "exercise":
        if (!value) {
          errors.exercise = "Please select exercise frequency";
        }
        break;
      case "drinking":
        if (!value) {
          errors.drinking = "Please select drinking frequency";
        }
        break;
      case "smoking":
        if (!value) {
          errors.smoking = "Please select smoking frequency";
        }
        break;
      case "pets":
        if (!value) {
          errors.pets = "Please select pet preference";
        }
        break;
      case "educationLevel":
        if (!value) {
          errors.educationLevel = "Please select education level";
        }
        break;
      case "maritalStatus":
        if (!value) {
          errors.maritalStatus = "Please select marital status";
        }
        break;
      case "kids":
        if (!value) {
          errors.kids = "Please select kids preference";
        }
        break;
      case "religion":
        if (!value || value.trim().length < 2) {
          errors.religion = "Religion must be at least 2 characters";
        }
        break;
      default:
        break;
    }

    return errors;
  };

  const validateStep = (currentStep) => {
    const errors = {};

    if (currentStep === 0) {
      Object.assign(errors, validateField("name", name));
      Object.assign(errors, validateField("age", age));
      Object.assign(errors, validateField("gender", gender));
      Object.assign(errors, validateField("lookingFor", lookingFor));
      Object.assign(errors, validateField("ethnicities", ethnicities));
      Object.assign(errors, validateField("nationalities", nationalities));
      Object.assign(errors, validateField("location", location));
      Object.assign(errors, validateField("languages", languages));
    }

    if (currentStep === 1) {
      Object.assign(errors, validateField("aboutMe", aboutMe));
      Object.assign(errors, validateField("height", height));
      Object.assign(errors, validateField("weight", weight));
      Object.assign(
        errors,
        validateField("sexualOrientation", sexualOrientation),
      );
      Object.assign(errors, validateField("starSign", starSign));
      Object.assign(errors, validateField("photos", photos));
    }

    if (currentStep === 2) {
      Object.assign(errors, validateField("exercise", exercise));
      Object.assign(errors, validateField("drinking", drinking));
      Object.assign(errors, validateField("smoking", smoking));
      Object.assign(errors, validateField("pets", pets));
    }

    if (currentStep === 3) {
      Object.assign(errors, validateField("educationLevel", educationLevel));
      Object.assign(errors, validateField("maritalStatus", maritalStatus));
      Object.assign(errors, validateField("kids", kids));
      Object.assign(errors, validateField("religion", religion));
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError(Object.values(errors)[0]);
      return false;
    }

    setError("");
    return true;
  };

  const handleFieldBlur = (fieldName, value) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    const errors = validateField(fieldName, value);
    setFieldErrors((prev) => ({ ...prev, ...errors }));
  };

  const isStepValid = (currentStep) => {
    if (currentStep === 0) {
      return (
        name.trim().length >= 2 &&
        age &&
        Number(age) >= 18 &&
        Number(age) <= 100 &&
        gender &&
        lookingFor &&
        ethnicities.length >= 1 &&
        nationalities.length >= 1 &&
        location.country.trim() &&
        location.state.trim() &&
        location.city.trim() &&
        languages.length >= 1
      );
    }
    if (currentStep === 1) {
      return (
        aboutMe.trim().length >= 10 &&
        aboutMe.length <= aboutMeLimit &&
        height &&
        Number(height) >= 100 &&
        Number(height) <= 250 &&
        weight &&
        Number(weight) >= 30 &&
        Number(weight) <= 200 &&
        sexualOrientation &&
        starSign &&
        photos.length > 0
      );
    }
    if (currentStep === 2) {
      return exercise && drinking && smoking && pets;
    }
    if (currentStep === 3) {
      return (
        educationLevel &&
        maritalStatus &&
        kids &&
        religion &&
        religion.trim().length >= 2
      );
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const goBack = () => {
    setError("");
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate Step 0 (Identity & Background)
    if (!validateStep(0)) {
      setError(
        "Please complete Step 1: Fill in Name, Age, Gender, Preference, Location, and select at least 1 Language, Ethnicity, and Nationality",
      );
      setStep(0);
      return;
    }

    // Validate Step 1 (About Me)
    if (!validateStep(1)) {
      setError(
        "Please complete Step 2: About Me, Height, Weight, Sexual Orientation, Star Sign, and Photos",
      );
      setStep(1);
      return;
    }

    // Validate Step 2 (Lifestyle)
    if (!validateStep(2)) {
      setError(
        "Please complete Step 3: Exercise, Drinking, Smoking, and Pets preferences",
      );
      setStep(2);
      return;
    }

    // Validate Step 3 (Personal Details)
    if (!validateStep(3)) {
      setError(
        "Please complete Step 4: Education Level, Marital Status, Kids, and Religion",
      );
      setStep(3);
      return;
    }

    setLoading(true);

    try {
      const profileData = {
        name,
        bio: aboutMe,
        aboutMe,
        age: parseInt(age),
        gender,
        lookingFor,
        photos,
        ethnicities,
        nationalities,
        location,
        languages,
        height: height ? parseInt(height) : undefined,
        weight: weight ? parseInt(weight) : undefined,
        sexualOrientation,
        starSign,
        exercise,
        drinking,
        smoking,
        pets,
        educationLevel,
        maritalStatus,
        kids,
        religion: religion.trim(),
      };

      console.log("[ProfileSetup] Submitting profile data:", profileData);
      const response = await profileAPI.createOrUpdate(profileData);
      console.log(
        "[ProfileSetup] Profile created/updated successfully:",
        response.data,
      );

      // Clear any old cached profile data to ensure fresh load
      localStorage.removeItem("profile");
      console.log("[ProfileSetup] Cleared old profile cache");

      updateUser({ isProfileComplete: true });
      navigate("/discovery");
    } catch (err) {
      console.error("[ProfileSetup] Error submitting profile:", err);
      console.error("[ProfileSetup] Error response:", err.response?.data);

      // Show the actual error message from backend
      const errorMessage =
        err.response?.data?.message ||
        "Failed to create profile. Please check all required fields.";
      setError(errorMessage);

      // If backend says languages required, go to step 0
      if (errorMessage.toLowerCase().includes("language")) {
        setStep(0);
      }

      setLoading(false);
    }
  };

  return (
    <AuthBackground>
      <LoadingAnimation isVisible={loading} />
      <div className="flex-1 flex items-center justify-center p-4 sm:p-5 relative">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-10 w-full max-w-xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] max-h-[90vh] overflow-y-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1 sm:mb-2 text-center">
            Complete Your Profile
          </h1>
          <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6 text-center">
            Step {step + 1} of {totalSteps} ·{" "}
            {
              [
                "Identity & Background",
                "About Me",
                "Lifestyle",
                "Personal Details",
              ][step]
            }
          </p>

          {error && (
            <div className="mb-4 sm:mb-5 rounded-lg sm:rounded-xl border-2 border-red-300 bg-red-50 px-4 sm:px-5 py-3 sm:py-4 text-xs sm:text-sm text-red-700 font-medium">
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{error}</span>
              </div>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 sm:gap-5"
          >
            {step === 0 && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col">
                  <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (touched.name) {
                        const errors = validateField("name", e.target.value);
                        setFieldErrors((prev) => ({
                          ...prev,
                          ...errors,
                          name: errors.name,
                        }));
                      }
                    }}
                    onBlur={() => handleFieldBlur("name", name)}
                    className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                      touched.name && fieldErrors.name
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                        : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                    }`}
                  />
                  {touched.name && fieldErrors.name && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {fieldErrors.name}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Age *
                    </label>
                    <input
                      type="number"
                      placeholder="Your age"
                      value={age}
                      onChange={(e) => {
                        setAge(e.target.value);
                        if (touched.age) {
                          const errors = validateField("age", e.target.value);
                          setFieldErrors((prev) => ({
                            ...prev,
                            ...errors,
                            age: errors.age,
                          }));
                        }
                      }}
                      onBlur={() => handleFieldBlur("age", age)}
                      min={18}
                      max={100}
                      className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        touched.age && fieldErrors.age
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                      }`}
                    />
                    {touched.age && fieldErrors.age && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {fieldErrors.age}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Gender *
                    </label>
                    <select
                      value={gender}
                      onChange={(e) => {
                        setGender(e.target.value);
                        if (touched.gender) {
                          const errors = validateField(
                            "gender",
                            e.target.value,
                          );
                          setFieldErrors((prev) => ({
                            ...prev,
                            ...errors,
                            gender: errors.gender,
                          }));
                        }
                      }}
                      onBlur={() => handleFieldBlur("gender", gender)}
                      className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        touched.gender && fieldErrors.gender
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                      }`}
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                    {touched.gender && fieldErrors.gender && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {fieldErrors.gender}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                    Looking For *
                  </label>
                  <select
                    value={lookingFor}
                    onChange={(e) => {
                      setLookingFor(e.target.value);
                      if (touched.lookingFor) {
                        const errors = validateField(
                          "lookingFor",
                          e.target.value,
                        );
                        setFieldErrors((prev) => ({
                          ...prev,
                          ...errors,
                          lookingFor: errors.lookingFor,
                        }));
                      }
                    }}
                    onBlur={() => handleFieldBlur("lookingFor", lookingFor)}
                    className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                      touched.lookingFor && fieldErrors.lookingFor
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                        : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                    }`}
                  >
                    <option value="">Select preference</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="everyone">Everyone</option>
                  </select>
                  {touched.lookingFor && fieldErrors.lookingFor && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {fieldErrors.lookingFor}
                    </p>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-2">
                    Location *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <input
                        type="text"
                        placeholder="Country"
                        value={location.country}
                        onChange={(e) => {
                          const newLocation = {
                            ...location,
                            country: e.target.value,
                          };
                          setLocation(newLocation);
                          if (touched.location) {
                            const errors = validateField(
                              "location",
                              newLocation,
                            );
                            setFieldErrors((prev) => ({ ...prev, ...errors }));
                          }
                        }}
                        onBlur={() => handleFieldBlur("location", location)}
                        className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                          touched.location && fieldErrors.location
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                            : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                        }`}
                      />
                    </div>
                    <div className="flex flex-col">
                      <input
                        type="text"
                        placeholder="State"
                        value={location.state}
                        onChange={(e) => {
                          const newLocation = {
                            ...location,
                            state: e.target.value,
                          };
                          setLocation(newLocation);
                          if (touched.location) {
                            const errors = validateField(
                              "location",
                              newLocation,
                            );
                            setFieldErrors((prev) => ({ ...prev, ...errors }));
                          }
                        }}
                        onBlur={() => handleFieldBlur("location", location)}
                        className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                          touched.location && fieldErrors.location
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                            : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                        }`}
                      />
                    </div>
                    <div className="flex flex-col">
                      <input
                        type="text"
                        placeholder="City"
                        value={location.city}
                        onChange={(e) => {
                          const newLocation = {
                            ...location,
                            city: e.target.value,
                          };
                          setLocation(newLocation);
                          if (touched.location) {
                            const errors = validateField(
                              "location",
                              newLocation,
                            );
                            setFieldErrors((prev) => ({ ...prev, ...errors }));
                          }
                        }}
                        onBlur={() => handleFieldBlur("location", location)}
                        className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                          touched.location && fieldErrors.location
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                            : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                        }`}
                      />
                    </div>
                  </div>
                  {touched.location && fieldErrors.location && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {fieldErrors.location}
                    </p>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-2">
                    Languages *
                  </label>
                  <div
                    className={`flex flex-wrap gap-2 p-3 rounded-lg border-2 ${
                      touched.languages && fieldErrors.languages
                        ? "border-red-500 bg-red-50/30"
                        : "border-transparent"
                    }`}
                  >
                    {LANGUAGE_OPTIONS.map((option) => {
                      const isSelected = languages.includes(option);
                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() => {
                            toggleSelection(option, languages, setLanguages);
                            setTouched((prev) => ({
                              ...prev,
                              languages: true,
                            }));
                          }}
                          className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${
                            isSelected
                              ? "bg-indigo-600 text-white border-indigo-600"
                              : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300"
                          }`}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                  {touched.languages && fieldErrors.languages && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {fieldErrors.languages}
                    </p>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-2">
                    Ethnicities *
                  </label>
                  <div
                    className={`flex flex-wrap gap-2 p-3 rounded-lg border-2 ${
                      touched.ethnicities && fieldErrors.ethnicities
                        ? "border-red-500 bg-red-50/30"
                        : "border-transparent"
                    }`}
                  >
                    {ETHNICITY_OPTIONS.map((option) => {
                      const isSelected = ethnicities.includes(option);
                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() => {
                            toggleSelection(
                              option,
                              ethnicities,
                              setEthnicities,
                            );
                            setTouched((prev) => ({
                              ...prev,
                              ethnicities: true,
                            }));
                          }}
                          className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${
                            isSelected
                              ? "bg-indigo-600 text-white border-indigo-600"
                              : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300"
                          }`}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                  {touched.ethnicities && fieldErrors.ethnicities && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {fieldErrors.ethnicities}
                    </p>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-2">
                    Nationalities *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add nationality"
                      value={nationalityInput}
                      onChange={(e) => setNationalityInput(e.target.value)}
                      className="flex-1 rounded-lg sm:rounded-xl border-2 border-gray-200 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/10"
                    />
                    <button
                      type="button"
                      onClick={addNationality}
                      className="rounded-lg sm:rounded-xl bg-indigo-600 text-white px-4 text-sm font-semibold"
                    >
                      Add
                    </button>
                  </div>
                  {nationalities.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {nationalities.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => {
                            const newList = nationalities.filter(
                              (value) => value !== item,
                            );
                            setNationalities(newList);
                            if (touched.nationalities) {
                              const errors = validateField(
                                "nationalities",
                                newList,
                              );
                              setFieldErrors((prev) => ({
                                ...prev,
                                ...errors,
                              }));
                            }
                          }}
                          className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700"
                        >
                          {item} ×
                        </button>
                      ))}
                    </div>
                  )}
                  {touched.nationalities && fieldErrors.nationalities && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {fieldErrors.nationalities}
                    </p>
                  )}
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col">
                  <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                    About Me
                  </label>
                  <textarea
                    placeholder="Tell us about yourself..."
                    value={aboutMe}
                    onChange={(e) => {
                      setAboutMe(e.target.value);
                      if (touched.aboutMe) {
                        const errors = validateField("aboutMe", e.target.value);
                        setFieldErrors((prev) => ({ ...prev, ...errors }));
                      }
                    }}
                    onBlur={() => handleFieldBlur("aboutMe", aboutMe)}
                    maxLength={aboutMeLimit}
                    rows={4}
                    className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base resize-y min-h-[120px] focus:outline-none focus:ring-2 ${
                      touched.aboutMe && fieldErrors.aboutMe
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                        : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                    }`}
                  />
                  <div className="flex items-center justify-between mt-1">
                    {touched.aboutMe && fieldErrors.aboutMe ? (
                      <p className="text-xs sm:text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {fieldErrors.aboutMe}
                      </p>
                    ) : (
                      <span></span>
                    )}
                    <small className="text-xs text-gray-400">
                      {aboutMe.length}/{aboutMeLimit}
                    </small>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Height (cm)
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 175"
                      value={height}
                      onChange={(e) => {
                        setHeight(e.target.value);
                        if (touched.height) {
                          const errors = validateField(
                            "height",
                            e.target.value,
                          );
                          setFieldErrors((prev) => ({ ...prev, ...errors }));
                        }
                      }}
                      onBlur={() => handleFieldBlur("height", height)}
                      min={100}
                      max={250}
                      className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        touched.height && fieldErrors.height
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                      }`}
                    />
                    {touched.height && fieldErrors.height && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {fieldErrors.height}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 70"
                      value={weight}
                      onChange={(e) => {
                        setWeight(e.target.value);
                        if (touched.weight) {
                          const errors = validateField(
                            "weight",
                            e.target.value,
                          );
                          setFieldErrors((prev) => ({ ...prev, ...errors }));
                        }
                      }}
                      onBlur={() => handleFieldBlur("weight", weight)}
                      min={30}
                      max={200}
                      className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        touched.weight && fieldErrors.weight
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                      }`}
                    />
                    {touched.weight && fieldErrors.weight && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {fieldErrors.weight}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Sexual Orientation *
                    </label>
                    <select
                      value={sexualOrientation}
                      onChange={(e) => {
                        setSexualOrientation(e.target.value);
                        if (touched.sexualOrientation) {
                          const errors = validateField(
                            "sexualOrientation",
                            e.target.value,
                          );
                          setFieldErrors((prev) => ({ ...prev, ...errors }));
                        }
                      }}
                      onBlur={() =>
                        handleFieldBlur("sexualOrientation", sexualOrientation)
                      }
                      className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        touched.sexualOrientation &&
                        fieldErrors.sexualOrientation
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                      }`}
                    >
                      <option value="">Select</option>
                      {SEXUAL_ORIENTATION_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {touched.sexualOrientation &&
                      fieldErrors.sexualOrientation && (
                        <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          {fieldErrors.sexualOrientation}
                        </p>
                      )}
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Star Sign *
                    </label>
                    <select
                      value={starSign}
                      onChange={(e) => {
                        setStarSign(e.target.value);
                        if (touched.starSign) {
                          const errors = validateField(
                            "starSign",
                            e.target.value,
                          );
                          setFieldErrors((prev) => ({ ...prev, ...errors }));
                        }
                      }}
                      onBlur={() => handleFieldBlur("starSign", starSign)}
                      className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        touched.starSign && fieldErrors.starSign
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                      }`}
                    >
                      <option value="">Select</option>
                      {STAR_SIGN_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {touched.starSign && fieldErrors.starSign && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {fieldErrors.starSign}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-2">
                    Photos * (1-5 photos)
                  </label>
                  <div
                    className={`grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 sm:gap-3 mt-2 p-3 rounded-lg border-2 ${
                      touched.photos && fieldErrors.photos
                        ? "border-red-500 bg-red-50/30"
                        : "border-transparent"
                    }`}
                  >
                    {photos.map((photo, index) => (
                      <div
                        key={index}
                        className="relative aspect-square rounded-lg sm:rounded-xl overflow-hidden bg-gray-100"
                      >
                        <img
                          src={photo}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute top-1 sm:top-2 right-1 sm:right-2 w-6 sm:w-7 h-6 sm:h-7 rounded-full bg-black/70 text-white flex items-center justify-center text-base sm:text-lg leading-none hover:bg-black/90"
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    {photos.length < 5 && (
                      <label className="aspect-square rounded-lg sm:rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer transition hover:border-indigo-400 hover:bg-indigo-50">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          disabled={uploading}
                          className="hidden"
                        />
                        <div className="text-3xl sm:text-4xl text-gray-400 font-light">
                          {uploading ? "..." : "+"}
                        </div>
                      </label>
                    )}
                  </div>
                  {touched.photos && fieldErrors.photos && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {fieldErrors.photos}
                    </p>
                  )}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Exercise *
                    </label>
                    <select
                      value={exercise}
                      onChange={(e) => {
                        setExercise(e.target.value);
                        if (touched.exercise) {
                          const errors = validateField(
                            "exercise",
                            e.target.value,
                          );
                          setFieldErrors((prev) => ({ ...prev, ...errors }));
                        }
                      }}
                      onBlur={() => handleFieldBlur("exercise", exercise)}
                      className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        touched.exercise && fieldErrors.exercise
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                      }`}
                    >
                      <option value="">Select</option>
                      {EXERCISE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {touched.exercise && fieldErrors.exercise && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {fieldErrors.exercise}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Drinking *
                    </label>
                    <select
                      value={drinking}
                      onChange={(e) => {
                        setDrinking(e.target.value);
                        if (touched.drinking) {
                          const errors = validateField(
                            "drinking",
                            e.target.value,
                          );
                          setFieldErrors((prev) => ({ ...prev, ...errors }));
                        }
                      }}
                      onBlur={() => handleFieldBlur("drinking", drinking)}
                      className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        touched.drinking && fieldErrors.drinking
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                      }`}
                    >
                      <option value="">Select</option>
                      {DRINKING_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {touched.drinking && fieldErrors.drinking && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {fieldErrors.drinking}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Smoking *
                    </label>
                    <select
                      value={smoking}
                      onChange={(e) => {
                        setSmoking(e.target.value);
                        if (touched.smoking) {
                          const errors = validateField(
                            "smoking",
                            e.target.value,
                          );
                          setFieldErrors((prev) => ({ ...prev, ...errors }));
                        }
                      }}
                      onBlur={() => handleFieldBlur("smoking", smoking)}
                      className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        touched.smoking && fieldErrors.smoking
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                      }`}
                    >
                      <option value="">Select</option>
                      {SMOKING_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {touched.smoking && fieldErrors.smoking && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {fieldErrors.smoking}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Pets *
                    </label>
                    <select
                      value={pets}
                      onChange={(e) => {
                        setPets(e.target.value);
                        if (touched.pets) {
                          const errors = validateField("pets", e.target.value);
                          setFieldErrors((prev) => ({ ...prev, ...errors }));
                        }
                      }}
                      onBlur={() => handleFieldBlur("pets", pets)}
                      className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        touched.pets && fieldErrors.pets
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                      }`}
                    >
                      <option value="">Select</option>
                      {PET_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {touched.pets && fieldErrors.pets && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {fieldErrors.pets}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Education Level *
                    </label>
                    <select
                      value={educationLevel}
                      onChange={(e) => {
                        setEducationLevel(e.target.value);
                        if (touched.educationLevel) {
                          const errors = validateField(
                            "educationLevel",
                            e.target.value,
                          );
                          setFieldErrors((prev) => ({ ...prev, ...errors }));
                        }
                      }}
                      onBlur={() =>
                        handleFieldBlur("educationLevel", educationLevel)
                      }
                      className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        touched.educationLevel && fieldErrors.educationLevel
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                      }`}
                    >
                      <option value="">Select</option>
                      {EDUCATION_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option.replace("_", " ")}
                        </option>
                      ))}
                    </select>
                    {touched.educationLevel && fieldErrors.educationLevel && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {fieldErrors.educationLevel}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Marital Status *
                    </label>
                    <select
                      value={maritalStatus}
                      onChange={(e) => {
                        setMaritalStatus(e.target.value);
                        if (touched.maritalStatus) {
                          const errors = validateField(
                            "maritalStatus",
                            e.target.value,
                          );
                          setFieldErrors((prev) => ({ ...prev, ...errors }));
                        }
                      }}
                      onBlur={() =>
                        handleFieldBlur("maritalStatus", maritalStatus)
                      }
                      className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        touched.maritalStatus && fieldErrors.maritalStatus
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                      }`}
                    >
                      <option value="">Select</option>
                      {MARITAL_STATUS_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {touched.maritalStatus && fieldErrors.maritalStatus && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {fieldErrors.maritalStatus}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Kids *
                    </label>
                    <select
                      value={kids}
                      onChange={(e) => {
                        setKids(e.target.value);
                        if (touched.kids) {
                          const errors = validateField("kids", e.target.value);
                          setFieldErrors((prev) => ({ ...prev, ...errors }));
                        }
                      }}
                      onBlur={() => handleFieldBlur("kids", kids)}
                      className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        touched.kids && fieldErrors.kids
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                      }`}
                    >
                      <option value="">Select</option>
                      {KIDS_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option.replace("_", " ")}
                        </option>
                      ))}
                    </select>
                    {touched.kids && fieldErrors.kids && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {fieldErrors.kids}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">
                      Religion *
                    </label>
                    <input
                      type="text"
                      placeholder="Your religion"
                      value={religion}
                      onChange={(e) => {
                        setReligion(e.target.value);
                        if (touched.religion) {
                          const errors = validateField(
                            "religion",
                            e.target.value,
                          );
                          setFieldErrors((prev) => ({ ...prev, ...errors }));
                        }
                      }}
                      onBlur={() => handleFieldBlur("religion", religion)}
                      className={`rounded-lg sm:rounded-xl border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        touched.religion && fieldErrors.religion
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/10"
                      }`}
                    />
                    {touched.religion && fieldErrors.religion && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {fieldErrors.religion}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={goBack}
                disabled={step === 0}
                className="rounded-lg sm:rounded-xl border border-gray-200 px-4 py-2 sm:py-3 text-sm sm:text-base font-semibold text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Back
              </button>
              {step < totalSteps - 1 ? (
                <button
                  type="button"
                  onClick={goNext}
                  disabled={!isStepValid(step)}
                  className="rounded-lg sm:rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold px-4 py-2 sm:py-3 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-500 transition-all"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading || uploading || !isStepValid(step)}
                  className="rounded-lg sm:rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold px-4 py-2 sm:py-3 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-500 transition-all"
                >
                  {loading ? "Creating Profile..." : "Complete Profile"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </AuthBackground>
  );
}

export default ProfileSetup;
